import { JourneyPhase } from '@/hooks/useJourneyProgress';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { Heart, Target, Sparkles, Map, CheckCircle2, Users, Check, Home, Trophy, Flame, Circle, Lock } from 'lucide-react';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Heart,
  Target,
  Sparkles,
  Map,
  CheckCircle2,
  Users,
  Home,
};

// Check if all habits for today are completed
const checkAllHabitsCompletedToday = (phases: JourneyPhase[]): boolean => {
  const habitsPhase = phases.find(p => p.id === 'habits');
  return habitsPhase?.progress === 100;
};

// Get milestone badge based on journey progress
const getMilestoneBadge = (progress: number): { 
  label: string; 
  Icon: React.ComponentType<{ className?: string }> | null;
  colorClass: string;
} => {
  if (progress >= 100) return { label: 'Journey Complete', Icon: Trophy, colorClass: 'text-yellow-500' };
  if (progress >= 80) return { label: 'Habit Streak', Icon: Flame, colorClass: 'text-orange-500' };
  if (progress >= 60) return { label: 'Plan Generated', Icon: Map, colorClass: 'text-accent' };
  if (progress >= 40) return { label: 'Goals Set', Icon: Target, colorClass: 'text-primary' };
  if (progress >= 20) return { label: 'Values Complete', Icon: CheckCircle2, colorClass: 'text-primary' };
  return { label: 'Getting Started', Icon: null, colorClass: 'text-muted-foreground' };
};

// Get phase completion status for tooltip
interface PhaseStatus {
  name: string;
  isComplete: boolean;
  detail?: string;
}

const getPhaseStatuses = (phases: JourneyPhase[]): PhaseStatus[] => {
  const valuesPhase = phases.find(p => p.id === 'values');
  const goalsPhase = phases.find(p => p.id === 'goals');
  const planPhase = phases.find(p => p.id === 'plan');
  const habitsPhase = phases.find(p => p.id === 'habits');
  const mentorshipPhase = phases.find(p => p.id === 'mentorship');

  return [
    {
      name: 'Values discovered',
      isComplete: valuesPhase?.isComplete ?? false,
    },
    {
      name: 'Goals set',
      isComplete: goalsPhase?.isComplete ?? false,
      detail: goalsPhase?.progress ? `${Math.round(goalsPhase.progress / 25)} active` : undefined
    },
    {
      name: 'Development plan generated',
      isComplete: planPhase?.isComplete ?? false,
    },
    {
      name: 'Habits started',
      isComplete: (habitsPhase?.progress ?? 0) > 0,
      detail: habitsPhase?.progress ? `${habitsPhase.progress}% today` : undefined
    },
    {
      name: 'Mentorship connected',
      isComplete: mentorshipPhase?.isComplete ?? false,
    },
  ];
};

interface JourneyNavigationProps {
  phases: JourneyPhase[];
  overallProgress: number;
  activeTab: string;
  onTabChange: (tabValue: string) => void;
  showHomeTab?: boolean;
  allHabitsCompletedToday?: boolean;
}

// Visible tabs: 4 core tabs (+ Home)
const VISIBLE_TABS = new Set(['home', 'goals', 'plan', 'habits']);

export const JourneyNavigation = ({
  phases,
  overallProgress,
  activeTab,
  onTabChange,
  showHomeTab = false,
  allHabitsCompletedToday,
}: JourneyNavigationProps) => {
  // Check if all habits are completed today (for Home tab checkmark)
  const habitsCompleteToday = allHabitsCompletedToday ?? checkAllHabitsCompletedToday(phases);

  // Values-first gating: Goals tab and Coach button require values completion
  const valuesPhase = phases.find(p => p.id === 'values');
  const valuesComplete = valuesPhase?.isComplete ?? false;

  // Create home phase if needed
  const homePhase: JourneyPhase | null = showHomeTab ? {
    id: 'home',
    name: 'Home',
    shortName: 'Home',
    description: 'Your career development dashboard',
    icon: 'Home',
    tabValue: 'home',
    order: 0,
    isComplete: true, // Home is a dashboard link, not a journey gate — always "complete" for unlock logic
    progress: 100,
    nextStepPrompt: 'View your overall progress and next steps.',
    requiredForNext: false,
  } : null;

  // Combine home with other phases, filter to visible tabs
  const combinedPhases = homePhase ? [homePhase, ...phases] : phases;
  const allPhases = combinedPhases.filter(p => VISIBLE_TABS.has(p.tabValue));

  return (
    <div className="w-full" data-tour="journey-progress">
      {/* Compact Navigation Strip */}
      <div className="bg-muted/30 rounded-xl p-2 border border-border/50">
        <div className="flex items-center gap-1 sm:gap-2">
          <TooltipProvider delayDuration={200}>
            {allPhases.map((phase, index) => {
              const Icon = iconMap[phase.icon] || Target;
              const isActive = activeTab === phase.tabValue;
              const isPreviousComplete = index === 0
                || allPhases[index - 1].isComplete;
              const isGoalsTab = phase.tabValue === 'goals';
              const isLocked = isGoalsTab
                ? !valuesComplete
                : (index !== 0 && !isPreviousComplete && !phase.isComplete);

              // SVG progress ring calculations
              const radius = 18;
              const circumference = 2 * Math.PI * radius;
              const progressOffset = circumference - (phase.progress / 100) * circumference;

              return (
                <Tooltip key={phase.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        onTabChange(phase.tabValue);
                      }}
                      data-tour={`tab-${phase.tabValue}`}
                      className={cn(
                        "relative flex flex-1 flex-col items-center gap-1.5 py-2 px-1 sm:px-3 rounded-lg",
                        "transition-all duration-300 ease-out",
                        "hover:bg-background/80 focus:outline-none focus:ring-2 focus:ring-primary/50",
                        isActive && "bg-background shadow-sm scale-[1.02]",
                        isLocked && "opacity-60"
                      )}
                    >
                      {/* Icon with Progress Ring */}
                      <div className={cn(
                        "relative w-10 h-10 sm:w-11 sm:h-11 transition-transform duration-300 ease-out",
                        isActive && "scale-110"
                      )}>
                        {/* Background ring */}
                        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 44 44">
                          <circle
                            cx="22"
                            cy="22"
                            r={radius}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            className="text-muted-foreground/20 transition-colors duration-300"
                          />
                        </svg>
                        
                        {/* Progress ring with smooth animation */}
                        <svg 
                          className="absolute inset-0 w-full h-full -rotate-90" 
                          viewBox="0 0 44 44"
                        >
                          <circle
                            cx="22"
                            cy="22"
                            r={radius}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={progressOffset}
                            className={cn(
                              "transition-all duration-700 ease-out",
                              phase.isComplete ? "text-primary" : "text-primary/70"
                            )}
                            style={{
                              transitionProperty: 'stroke-dashoffset, color',
                            }}
                          />
                        </svg>
                        
                        {/* Center icon with completion animation */}
                        <div
                          className={cn(
                            "absolute inset-1 rounded-full flex items-center justify-center",
                            "transition-all duration-300 ease-out",
                            phase.isComplete 
                              ? "bg-primary text-primary-foreground scale-100" 
                              : isActive 
                                ? "bg-primary/15 text-primary"
                                : "bg-transparent text-muted-foreground"
                          )}
                        >
                          <div className={cn(
                            "transition-all duration-300",
                            phase.isComplete && "animate-scale-in"
                          )}>
                            {isLocked ? (
                              <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                            ) : phase.isComplete ? (
                              <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                            ) : (
                              <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                            )}
                          </div>
                        </div>
                        
                        {/* Pulse effect for active incomplete phase */}
                        {isActive && !phase.isComplete && (
                          <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" 
                               style={{ animationDuration: '2s' }} />
                        )}
                      </div>
                      
                      {/* Phase Label */}
                      <span className={cn(
                        "text-[10px] sm:text-xs font-medium leading-tight text-center",
                        "transition-colors duration-200 ease-out",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}>
                        {phase.shortName}
                      </span>
                      
                      {/* Active indicator line - 3px thick purple bottom border */}
                      <div className={cn(
                        "absolute bottom-0 left-1/2 -translate-x-1/2 h-[3px] bg-primary rounded-full",
                        "transition-all duration-200 ease-out",
                        isActive ? "w-8 sm:w-10 opacity-100" : "w-0 opacity-0"
                      )} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs animate-fade-in">
                    <div className="space-y-1.5">
                      <p className="font-semibold">{phase.name}</p>
                      {isLocked ? (
                        <p className="text-xs text-muted-foreground">
                          Complete {isGoalsTab ? 'Values Discovery' : allPhases[index - 1]?.name || 'the previous step'} to unlock
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-muted-foreground">{phase.description}</p>
                          <div className="flex items-center gap-2 pt-1">
                            <Progress value={phase.progress} className="h-1.5 flex-1" />
                            <span className="text-xs font-medium">{phase.progress}%</span>
                          </div>
                          {!phase.isComplete && phase.nextStepPrompt && (
                            <p className="text-xs text-primary/80 pt-1 border-t border-border/50">
                              {phase.nextStepPrompt}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </TooltipProvider>

          {/* Overall Progress Indicator with Milestone Badge */}
          {(() => {
            const milestone = getMilestoneBadge(overallProgress);
            const phaseStatuses = getPhaseStatuses(phases);
            const MilestoneIcon = milestone.Icon;
            
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 pl-3 border-l border-border/50 cursor-pointer hover:bg-background/50 rounded-lg px-2 py-1 -mr-1 transition-colors shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground hidden sm:block">Journey</div>
                      <div className="flex items-center gap-1.5 justify-end">
                        <span className="text-sm font-bold text-primary transition-all duration-500">
                          {overallProgress}%
                        </span>
                        {MilestoneIcon && (
                          <MilestoneIcon 
                            className={cn(
                              "w-4 h-4 animate-fade-in",
                              milestone.colorClass
                            )} 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  side="bottom" 
                  align="end"
                  className="w-64 p-0 bg-card border border-border/50"
                >
                  <div className="p-3 border-b border-border/50">
                    <div className="flex items-center gap-2">
                      {MilestoneIcon && (
                        <MilestoneIcon className={cn("w-5 h-5", milestone.colorClass)} />
                      )}
                      <div>
                        <p className="font-semibold text-sm">{milestone.label}</p>
                        <p className="text-xs text-muted-foreground">Your Progress Breakdown</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 space-y-2">
                    {phaseStatuses.map((status, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {status.isComplete ? (
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                        )}
                        <span className={cn(
                          status.isComplete ? "text-foreground" : "text-muted-foreground"
                        )}>
                          {status.name}
                          {status.detail && (
                            <span className="text-muted-foreground ml-1">
                              ({status.detail})
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-t border-border/50 bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Progress value={overallProgress} className="flex-1 h-1.5" />
                      <span className="text-xs font-medium text-primary">{overallProgress}%</span>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
